/**
 * Shared dialog helpers for the admin back office.
 *
 * UNTRUSTED TEXT RULE (applies to every option in this file): SweetAlert2
 * renders several of its options as HTML, not as text. A string handed to
 * `html` or `title` is parsed with DOMParser and the parsed nodes are moved
 * into the live document, so a server-originated message carrying markup
 * would be able to run in the operator's browser. Server text is therefore
 * never passed to those options as a string - it is rendered as a DOM
 * element whose `textContent` carries the text (see `inertTextElement`).
 */

/**
 * The text shown when a failure carries no message an operator can read.
 *
 * WHY a shared constant: the wording matches the readability guard in
 * `errorHandler.tomcatError`, so the operator reads the same sentence
 * whether the unreadable payload is caught there or here.
 */
const UNREADABLE_FAILURE_TEXT = `An unexpected error occurred. Please try again.`;

/**
 * Reduce any failure value to the plain text an operator should read.
 *
 * WHY this exists (TicketApp #29691, finding C5-INJECT-2): `throwErr` is the
 * one place every failed request reaches, so it is the last place where a
 * payload that is not text can still be turned into something readable.
 * Coercing an object into a template literal yields the literal string
 * "[object Object]", which tells the operator nothing. `errorHandler`
 * already guarantees a string today (SPEC §4.3 D3), so this is cheap
 * defence for a future caller that hands `throwErr` something else.
 *
 * @param {*} value - The failure payload as received.
 * @returns {string} Readable plain text. Never "[object Object]".
 */
function readableFailureText(value) {
    if (typeof value === `string`) {
        // A payload an upstream caller already coerced to text carries no
        // information either, so it is treated as unreadable.
        return value === `[object Object]` ? UNREADABLE_FAILURE_TEXT : value;
    }
    if (value === null || value === undefined) {
        return UNREADABLE_FAILURE_TEXT;
    }
    if (value instanceof Error) {
        // `String(error)` is what this dialog used to show ("Error: boom"),
        // so an Error instance keeps the wording the operator saw before.
        return String(value);
    }
    if (typeof value === `object`) {
        // The backend's legacy error body shape (SPEC §4.3 D3) and the
        // JavaScript `Error` convention are both read before falling back to
        // a serialisation, so the operator still sees WHY it failed.
        const extracted = typeof value.Message === `string` && value.Message !== ``
            ? value.Message
            : (typeof value.message === `string` && value.message !== `` ? value.message : ``);
        if (extracted !== ``) {
            return extracted;
        }
        try {
            // `JSON.stringify` throws on a circular structure, hence the guard.
            const serialised = JSON.stringify(value);
            if (serialised !== undefined && serialised !== `{}`) {
                return serialised;
            }
        } catch {
            // A circular payload has no readable serialisation; the fallback
            // below covers it.
        }
        return UNREADABLE_FAILURE_TEXT;
    }
    return String(value);
}

/**
 * Build a DOM element that carries a message as TEXT.
 *
 * WHY a DOM element and never a string (TicketApp #29691, finding
 * C5-INJECT-2): every SweetAlert2 option that renders a string is an HTML
 * sink - `html` and `title` are both routed through `parseHtmlToContainer`,
 * which parses a string with DOMParser and moves the parsed nodes into the
 * live document. A server-originated message carrying markup
 * (`<img src=x onerror=...>`) therefore executed its handler in a real
 * browser. An element is appended verbatim instead, so the same text is on
 * screen while the markup stays inert. This is the pattern proven for the
 * reset screen in commit d2f9e90 (finding C5-INJECT-1), applied here to the
 * shared dialog.
 *
 * @param {string} text - The plain text to carry.
 * @returns {HTMLElement} A span whose text content is `text`.
 */
function inertTextElement(text) {
    const element = document.createElement(`span`);
    element.textContent = text;
    return element;
}

export const responses = {
    /**
     * Open the shared error dialog for a failed request.
     *
     * UNTRUSTED TEXT RULE: the message and the heading may both originate on
     * the server, so neither is ever handed to SweetAlert2 as a string.
     * SweetAlert2's `html` and `title` options are HTML sinks - a string is
     * parsed with DOMParser and the parsed nodes are moved into the live
     * document - so a server-originated message carrying markup would be
     * able to run in the operator's browser (TicketApp #29691, finding
     * C5-INJECT-2, measured `window.__PWNED === 1` in a real browser). Both
     * are therefore rendered as DOM elements whose `textContent` carries the
     * text, which SweetAlert2 appends verbatim: the same text on screen,
     * inert when the message is not plain text. The same rule holds for
     * every other SweetAlert2 option that renders a string - never pass a
     * server-originated string as HTML.
     *
     * @param {*} err - The failure payload, rendered as readable plain text.
     * @param {string} [title='An Error Occurred'] - The dialog heading.
     * @returns {Promise} The SweetAlert2 result promise for the dialog.
     */
    throwErr(err,title = `An Error Occurred`){
        const message = readableFailureText(err);
        const heading = title ? readableFailureText(title) : ``;
        return Swal.fire({
            title: heading === `` ? undefined : inertTextElement(heading),
            icon: `error`,
            html: message === `` ? undefined : inertTextElement(message),
            showCloseButton : true,
        })
    },

    /**
     * Show a success toast.
     *
     * UNTRUSTED TEXT RULE: the text is passed to SweetAlert2's `title`
     * option, which IS an HTML sink - a string is parsed with DOMParser and
     * the parsed nodes are moved into the live document. Only
     * application-authored text may be passed here; a server-originated
     * string must go through `throwErr`, which renders it as inert text.
     *
     * @param {string} msg - The application-authored message to show.
     * @returns {Promise} The SweetAlert2 result promise for the toast.
     */
    showSuccess(msg){
        return Toast.fire({
            icon: 'success',
            title: msg
        });
    },

    /**
     * Show an informational toast.
     *
     * UNTRUSTED TEXT RULE: the text is passed to SweetAlert2's `title`
     * option, which IS an HTML sink - a string is parsed with DOMParser and
     * the parsed nodes are moved into the live document. Only
     * application-authored text may be passed here; a server-originated
     * string must go through `throwErr`, which renders it as inert text.
     *
     * @param {string} msg - The application-authored message to show.
     * @returns {Promise} The SweetAlert2 result promise for the toast.
     */
    showInfo(msg){
        return Toast.fire({
            icon: 'info',
            title: msg
        });
    },

    /**
     * Show a warning toast.
     *
     * UNTRUSTED TEXT RULE: the text is passed to SweetAlert2's `title`
     * option, which IS an HTML sink - a string is parsed with DOMParser and
     * the parsed nodes are moved into the live document. Only
     * application-authored text may be passed here; a server-originated
     * string must go through `throwErr`, which renders it as inert text.
     *
     * @param {string} msg - The application-authored message to show.
     * @returns {Promise} The SweetAlert2 result promise for the toast.
     */
    showWarning(msg){
        return Toast.fire({
            icon: 'warning',
            title: msg
        });
    },
}
